import React from "react";
import { Box, Paper, SxProps, Theme, Typography } from "@mui/material";

/**
 * The superadmin panel's look: white cards on the grey page, slate text, one
 * blue accent and the usual status colours. Kept here so every superadmin
 * page and panel shares it instead of styling its own boxes.
 */
export const SA = {
  text: "#0F172A",
  textMuted: "#64748B",
  textSubtle: "#94A3B8",
  border: "#E2E8F0",
  divider: "#EEF2F6",
  surface: "#FFFFFF",
  surfaceMuted: "#F8FAFC",
  accent: "#2563EB",
  shadow: "0 1px 2px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04)",
  radius: "14px",
} as const;

export type Tone = "blue" | "green" | "amber" | "red" | "violet" | "slate" | "teal";

export const TONES: Record<Tone, { fg: string; bg: string; border: string }> = {
  blue: { fg: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE" },
  green: { fg: "#15803D", bg: "#F0FDF4", border: "#BBF7D0" },
  amber: { fg: "#B45309", bg: "#FFFBEB", border: "#FDE68A" },
  red: { fg: "#B91C1C", bg: "#FEF2F2", border: "#FECACA" },
  violet: { fg: "#6D28D9", bg: "#F5F3FF", border: "#DDD6FE" },
  slate: { fg: "#334155", bg: "#F1F5F9", border: "#E2E8F0" },
  teal: { fg: "#0F766E", bg: "#F0FDFA", border: "#99F6E4" },
};

/** The card surface on its own, for panels that lay out their own content. */
export const saCardSx = {
  bgcolor: SA.surface,
  border: `1px solid ${SA.border}`,
  borderRadius: SA.radius,
  boxShadow: SA.shadow,
} as const;

/** A tinted square holding an icon. */
export const IconTile: React.FC<{ icon: React.ReactNode; tone?: Tone; size?: number }> = ({
  icon,
  tone = "blue",
  size = 36,
}) => (
  <Box
    aria-hidden
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: "10px",
      display: "grid",
      placeItems: "center",
      bgcolor: TONES[tone].bg,
      color: TONES[tone].fg,
      "& svg": { fontSize: size * 0.55 },
    }}
  >
    {icon}
  </Box>
);

/** A card with an optional header (icon, title, subtitle, action on the right). */
export const SaCard: React.FC<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: Tone;
  action?: React.ReactNode;
  /** Red border and header for destructive settings. */
  danger?: boolean;
  /** Body without padding, for lists and tables that run edge to edge. */
  flush?: boolean;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ title, subtitle, icon, tone, action, danger, flush, children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      ...saCardSx,
      ...(danger ? { borderColor: TONES.red.border } : {}),
      overflow: "hidden",
      ...((sx as object) ?? {}),
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: children ? `1px solid ${danger ? TONES.red.border : SA.divider}` : 0,
          bgcolor: danger ? TONES.red.bg : undefined,
        }}
      >
        {icon && <IconTile icon={icon} tone={danger ? "red" : tone} size={32} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title && (
            <Typography
              component="h2"
              sx={{ fontWeight: 650, fontSize: "0.98rem", color: danger ? TONES.red.fg : SA.text }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography sx={{ fontSize: "0.82rem", color: SA.textMuted, mt: 0.25 }}>{subtitle}</Typography>
          )}
        </Box>
        {action}
      </Box>
    )}
    {children && <Box sx={flush ? undefined : { p: { xs: 2, sm: 2.5 } }}>{children}</Box>}
  </Paper>
);

/** One number with its label, an icon and an optional caption or warning. */
export const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tone?: Tone;
  caption?: React.ReactNode;
  /** Colours the caption, e.g. a high cancellation rate. */
  captionTone?: Tone;
  highlight?: boolean;
}> = ({ label, value, icon, tone = "blue", caption, captionTone, highlight }) => (
  <Paper
    elevation={0}
    sx={{
      ...saCardSx,
      ...(highlight ? { borderColor: TONES[tone].border } : {}),
      p: 2,
      display: "flex",
      flexDirection: "column",
      gap: 1.25,
      minWidth: 0,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <IconTile icon={icon} tone={tone} size={32} />
      <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: SA.textMuted }}>{label}</Typography>
    </Box>
    <Typography
      sx={{
        fontSize: "1.75rem",
        fontWeight: 700,
        lineHeight: 1.1,
        color: SA.text,
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "-0.01em",
      }}
    >
      {value}
    </Typography>
    {caption && (
      <Typography
        component="div"
        sx={{
          fontSize: "0.8rem",
          color: captionTone ? TONES[captionTone].fg : SA.textMuted,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {caption}
      </Typography>
    )}
  </Paper>
);

/** Responsive grid for KpiCards. */
export const KpiGrid: React.FC<{ children: React.ReactNode; min?: number }> = ({ children, min = 200 }) => (
  <Box
    sx={{
      display: "grid",
      gap: 1.5,
      gridTemplateColumns: {
        xs: "repeat(2, minmax(0, 1fr))",
        sm: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      },
    }}
  >
    {children}
  </Box>
);

/** A row inside a card: icon, title and description, and its control on the right. */
export const SettingRow: React.FC<{
  icon?: React.ReactNode;
  tone?: Tone;
  title: React.ReactNode;
  description?: React.ReactNode;
  control?: React.ReactNode;
  divider?: boolean;
}> = ({ icon, tone = "slate", title, description, control, divider }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      px: { xs: 2, sm: 2.5 },
      py: 1.75,
      borderTop: divider ? `1px solid ${SA.divider}` : 0,
      flexWrap: { xs: "wrap", sm: "nowrap" },
    }}
  >
    {icon && <IconTile icon={icon} tone={tone} size={36} />}
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.93rem", color: SA.text }}>{title}</Typography>
      {description && (
        <Typography sx={{ fontSize: "0.82rem", color: SA.textMuted, mt: 0.25, lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    {control && <Box sx={{ flexShrink: 0 }}>{control}</Box>}
  </Box>
);

/** A small rounded status label. */
export const StatusPill: React.FC<{ tone: Tone; children: React.ReactNode; dot?: boolean }> = ({
  tone,
  children,
  dot = true,
}) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.1,
      py: 0.35,
      borderRadius: 999,
      fontSize: "0.76rem",
      fontWeight: 600,
      whiteSpace: "nowrap",
      color: TONES[tone].fg,
      bgcolor: TONES[tone].bg,
      border: `1px solid ${TONES[tone].border}`,
    }}
  >
    {dot && <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />}
    {children}
  </Box>
);

/** The title row every superadmin page starts with. */
export const SaPageHeader: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: Tone;
  actions?: React.ReactNode;
}> = ({ title, subtitle, icon, tone = "blue", actions }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      gap: 1.75,
      mb: 3,
      flexWrap: "wrap",
    }}
  >
    {icon && <IconTile icon={icon} tone={tone} size={44} />}
    <Box sx={{ flex: 1, minWidth: 220 }}>
      <Typography
        component="h1"
        sx={{ fontWeight: 700, fontSize: { xs: "1.35rem", sm: "1.6rem" }, color: SA.text, letterSpacing: "-0.01em" }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: "0.9rem", color: SA.textMuted, mt: 0.25 }}>{subtitle}</Typography>
      )}
    </Box>
    {actions && <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>{actions}</Box>}
  </Box>
);

/** Head row of a table inside a SaCard. */
export const saTableHeadSx = {
  "& th": {
    bgcolor: SA.surfaceMuted,
    color: SA.textMuted,
    fontWeight: 600,
    fontSize: "0.8rem",
    borderBottom: `1px solid ${SA.border}`,
    whiteSpace: "nowrap",
  },
} as const;

/** Body rows of a table inside a SaCard. */
export const saTableBodySx = {
  "& td": { borderBottom: `1px solid ${SA.divider}`, color: SA.text, fontSize: "0.88rem" },
  "& tr:last-child td": { borderBottom: 0 },
} as const;

/** A small segmented control, e.g. this month / last month. */
export const saToggleSx = {
  bgcolor: SA.surface,
  "& .MuiToggleButton-root": {
    textTransform: "none",
    px: 1.5,
    py: 0.4,
    fontWeight: 600,
    color: SA.textMuted,
    borderColor: SA.border,
    "&.Mui-selected": { bgcolor: TONES.blue.bg, color: TONES.blue.fg },
    "&.Mui-selected:hover": { bgcolor: TONES.blue.bg },
  },
} as const;

export type SectionNavItem<K extends string> = {
  key: K;
  label: string;
  icon: React.ReactNode;
  /** Shown in red, e.g. the danger zone. */
  danger?: boolean;
};

/**
 * The sections of a page as a vertical list beside the content on wide
 * screens, and a row of scrollable pills on phones.
 */
export function SaSectionNav<K extends string>({
  items,
  value,
  onChange,
  title,
}: {
  items: readonly SectionNavItem<K>[];
  value: K;
  onChange: (key: K) => void;
  title?: string;
}) {
  return (
    <Box
      component="nav"
      aria-label={title || "Sections"}
      sx={{
        ...saCardSx,
        p: { xs: 0.75, md: 1 },
        display: "flex",
        flexDirection: { xs: "row", md: "column" },
        gap: 0.5,
        overflowX: { xs: "auto", md: "visible" },
        position: { md: "sticky" },
        top: { md: 88 },
      }}
    >
      {title && (
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            px: 1.25,
            pt: 0.75,
            pb: 0.5,
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: SA.textSubtle,
          }}
        >
          {title}
        </Typography>
      )}
      {items.map((item) => {
        const active = item.key === value;
        const tone = item.danger ? TONES.red : TONES.blue;
        return (
          <Box
            key={item.key}
            component="button"
            type="button"
            onClick={() => onChange(item.key)}
            aria-current={active ? "page" : undefined}
            sx={{
              all: "unset",
              boxSizing: "border-box",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexShrink: 0,
              px: 1.25,
              py: 1,
              borderRadius: "10px",
              fontSize: "0.9rem",
              fontWeight: active ? 650 : 500,
              whiteSpace: "nowrap",
              position: "relative",
              color: active ? tone.fg : item.danger ? TONES.red.fg : SA.textMuted,
              bgcolor: active ? tone.bg : "transparent",
              transition: "background-color 150ms, color 150ms",
              "&:hover": { bgcolor: active ? tone.bg : SA.surfaceMuted, color: active ? tone.fg : item.danger ? TONES.red.fg : SA.text },
              "&:focus-visible": { outline: `2px solid ${SA.accent}`, outlineOffset: 1 },
              "& svg": { fontSize: "1.15rem" },
              "&::before": {
                content: '""',
                display: { xs: "none", md: "block" },
                position: "absolute",
                left: 0,
                top: "22%",
                bottom: "22%",
                width: 3,
                borderRadius: 3,
                bgcolor: tone.fg,
                transform: active ? "scaleY(1)" : "scaleY(0)",
                transition: "transform 0.25s cubic-bezier(0.2, 0, 0, 1)",
              },
            }}
          >
            {item.icon}
            {item.label}
          </Box>
        );
      })}
    </Box>
  );
}
